import React, {useEffect, useState} from "react";
import Chart from "react-apexcharts";
import * as axios from "axios";
import {
  COMPARISONS, PERIODS, comparisonRange, dayLabel, monthOptions, periodRange, rangeLabel, todayBucharest, yearOptions,
} from "./periods";
import "./styles.css";

const baseUrl = "https://api.appetito.ro/";

// Statistici V2 (super admins): one period + a comparison period, every
// figure computed by the backend from live data (GET v1/admin/statistics-v2,
// see models/statisticsV2.go). Layout from the "Appetito Dashboard" spec.

const PARAM_KEYS = ["period", "from", "to", "cmp", "cmpFrom", "cmpMonth", "cmpYear", "fleetTab"];
const DEFAULTS = {period: "7d", cmp: "prev", fleetTab: "km"};

const lei = (v, decimals = 0) => `${Number(v || 0).toLocaleString("ro-RO", {minimumFractionDigits: decimals, maximumFractionDigits: decimals})} lei`;
const compactLei = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString("ro-RO", {maximumFractionDigits: 1})} k lei` : lei(v));
const int = (v) => Number(v || 0).toLocaleString("ro-RO");
const pct = (v, decimals = 0) => (v == null ? "—" : `${(v * 100).toLocaleString("ro-RO", {minimumFractionDigits: decimals, maximumFractionDigits: decimals})}%`);
const minutes = (v) => (v == null ? "—" : `${v.toLocaleString("ro-RO", {maximumFractionDigits: 1})} min`);
const formatDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("ro-RO", {day: "numeric", month: "short", timeZone: "Europe/Bucharest"});
};

function readParams(search) {
  const query = new URLSearchParams(search);
  const params = {...DEFAULTS};
  PARAM_KEYS.forEach((key) => {
    if (query.get(key)) {
      params[key] = query.get(key);
    }
  });
  return params;
}

// A change against the comparison: % for amounts, a difference for minutes
// and percentage points. `better`: "higher" or "lower".
function Change({current, previous, kind = "percent", better = "higher", available}) {
  if (!available || previous == null || current == null || (kind === "percent" && !previous)) {
    return <span className="sv2-change sv2-muted">fără date</span>;
  }
  let delta;
  let text;
  if (kind === "percent") {
    delta = current / previous - 1;
    text = `${delta > 0 ? "+" : ""}${(delta * 100).toLocaleString("ro-RO", {maximumFractionDigits: 1})}%`;
  } else if (kind === "minutes") {
    delta = current - previous;
    text = `${delta > 0 ? "+" : ""}${delta.toLocaleString("ro-RO", {maximumFractionDigits: 1})} min`;
  } else {
    delta = (current - previous) * 100;
    text = `${delta > 0 ? "+" : ""}${delta.toLocaleString("ro-RO", {maximumFractionDigits: 1})} pp`;
  }
  const good = better === "higher" ? delta > 0 : delta < 0;
  const tone = delta === 0 ? "sv2-muted" : good ? "sv2-good" : "sv2-bad";
  return <span className={`sv2-change ${tone}`}>{text}</span>;
}

function Delta({value}) {
  if (!value) {
    return <span className="sv2-muted">0</span>;
  }
  return <span className={value > 0 ? "sv2-good" : "sv2-bad"}>{value > 0 ? `+${value}` : value}</span>;
}

function BarCell({value, max, children}) {
  const width = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <td className="sv2-bar-cell">
      <div className="sv2-bar" style={{width: `${width}%`}}/>
      <span>{children}</span>
    </td>
  );
}

function Toggle({options, value, onChange}) {
  return (
    <div className="sv2-toggle">
      {options.map((o) => (
        <button key={o.id} className={o.id === value ? "active" : ""} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

export default function StatisticsV2({location, history}) {
  const [params, setParams] = useState(() => readParams(location.search));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [restaurantSort, setRestaurantSort] = useState("orders");
  const [customerSort, setCustomerSort] = useState("orders");
  const [categorySort, setCategorySort] = useState("qty");
  const [dataFrom, setDataFrom] = useState(null);
  const [allCategories, setAllCategories] = useState(false);

  const hi = todayBucharest();
  const [s, e] = periodRange(params, hi);
  const [cs, ce] = comparisonRange(params, s, e, dataFrom);
  const cmpAvailable = !dataFrom || ce >= dataFrom;
  // A period ending today is still in progress: the comparison's last day is
  // cut at the same time of day (refreshed with each load).
  const cutoff = e === hi
    ? new Intl.DateTimeFormat("en-GB", {timeZone: "Europe/Bucharest", hour: "2-digit", minute: "2-digit", hourCycle: "h23"}).format(new Date())
    : null;

  const update = (changes) => {
    const next = {...params, ...changes};
    setParams(next);
    const query = new URLSearchParams();
    PARAM_KEYS.forEach((key) => {
      if (next[key] && next[key] !== DEFAULTS[key]) {
        query.set(key, next[key]);
      }
    });
    history.replace({search: query.toString() ? `?${query}` : ""});
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const token = localStorage.getItem("access_token");
    axios.default.get(`${baseUrl}v1/admin/statistics-v2`, {
      params: {from: s, to: e, cmpFrom: cs, cmpTo: ce, ...(cutoff ? {cutoff} : {})},
      headers: {Authorization: `Bearer ${token}`},
    }).then((response) => {
      if (!cancelled) {
        setData(response.data);
        setDataFrom(response.data.data_from || null);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(err?.response?.data?.message || "Statisticile nu au putut fi încărcate.");
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // The cut-off is read when the period changes, not every minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, e, cs, ce]);

  const cmpLabel = `față de ${rangeLabel(cs, ce)}${cutoff ? ` (până la ${cutoff})` : ""}`;

  return (
    <div className="sv2-container">
      <div className="sv2-header">
        <div>
          <h2 className="sv2-title">Statistici V2</h2>
          {data && <Summary data={data} cmpLabel={cmpLabel} available={cmpAvailable}/>}
        </div>
        <div className="sv2-controls">
          <div className="sv2-control-row">
            <Toggle options={PERIODS} value={params.period} onChange={(period) => update({period})}/>
            {params.period === "custom" && (
              <div className="sv2-dates">
                <label>De la <input type="date" value={params.from || s} max={hi} onChange={(ev) => update({from: ev.target.value})}/></label>
                <label>Până la <input type="date" value={params.to || e} max={hi} onChange={(ev) => update({to: ev.target.value})}/></label>
              </div>
            )}
          </div>
          <div className="sv2-control-row">
            <span className="sv2-muted">Compară cu</span>
            <select value={params.cmp} onChange={(ev) => update({cmp: ev.target.value})}>
              {COMPARISONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {params.cmp === "month" && (
              <select value={params.cmpMonth || (monthOptions(s, dataFrom)[0] || {}).value || ""} onChange={(ev) => update({cmpMonth: ev.target.value})}>
                {monthOptions(s, dataFrom).map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            )}
            {params.cmp === "year" && (
              <select value={params.cmpYear || yearOptions(s, dataFrom)[0] || ""} onChange={(ev) => update({cmpYear: ev.target.value})}>
                {yearOptions(s, dataFrom).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {params.cmp === "custom" && (
              <label className="sv2-dates">începând cu <input type="date" value={params.cmpFrom || cs} onChange={(ev) => update({cmpFrom: ev.target.value})}/></label>
            )}
          </div>
          <div className="sv2-muted sv2-range-note">{rangeLabel(s, e)} · {cmpLabel}</div>
        </div>
      </div>

      {error && <div className="sv2-error">{error}</div>}
      {!data && loading && <div className="loader-container"><div className="loader"/></div>}

      {data && (
        <div className={loading ? "sv2-loading" : ""}>
          <Kpis data={data} cmpLabel={cmpLabel} available={cmpAvailable}/>
          <RevenueChart data={data} available={cmpAvailable} cmpRange={[cs, ce]}/>
          <div className="sv2-grid sv2-grid-3">
            <Restaurants rows={data.restaurants} sort={restaurantSort} onSort={setRestaurantSort} available={cmpAvailable}/>
            <Customers rows={data.customers} sort={customerSort} onSort={setCustomerSort}/>
            <Couriers rows={data.couriers} available={cmpAvailable}/>
          </div>
          <div className="sv2-grid sv2-grid-2">
            <Heatmap heatmap={data.current.heatmap}/>
            <Mix period={data.current}/>
          </div>
          <Categories categories={data.categories} sort={categorySort} onSort={setCategorySort}
                      showAll={allCategories} onShowAll={() => setAllCategories(true)}/>
          <Fleet cars={data.fleet} tab={params.fleetTab} onTab={(fleetTab) => update({fleetTab})} rangeLabel={rangeLabel(s, e)}/>
        </div>
      )}
    </div>
  );
}

function Summary({data, cmpLabel, available}) {
  const current = data.current;
  if (!current.kpis.delivered) {
    return <p className="sv2-summary">Nicio comandă livrată în această perioadă.</p>;
  }
  const previous = data.comparison.kpis.revenue;
  const change = available && previous ? current.kpis.revenue / previous - 1 : null;
  return (
    <p className="sv2-summary">
      <strong>{current.top_restaurant}</strong> conduce la comenzi.
      {current.peak_hour != null && <> Vârful de comenzi e la ora <strong>{String(current.peak_hour).padStart(2, "0")}:00</strong>.</>}
      {change != null && <> Încasări <strong className={change >= 0 ? "sv2-good" : "sv2-bad"}>{change >= 0 ? "+" : ""}{(change * 100).toLocaleString("ro-RO", {maximumFractionDigits: 1})}%</strong> {cmpLabel.replace(/\.$/, "")}.</>}
    </p>
  );
}

function Kpis({data, cmpLabel, available}) {
  const c = data.current.kpis;
  const p = data.comparison.kpis;
  const tiles = [
    {label: "Încasări", value: lei(c.revenue), cur: c.revenue, prev: p.revenue, hint: "Ce au plătit clienții pe comenzile livrate: produse + transport + taxe − voucher (fără bacșiș)."},
    {label: "Comenzi livrate", value: int(c.delivered), cur: c.delivered, prev: p.delivered},
    {label: "Valoarea medie a comenzii", value: lei(c.average_order, 2), cur: c.average_order, prev: p.average_order},
    {label: "Venit Appetito (comision + livrare)", value: lei(c.appetito_revenue), cur: c.appetito_revenue, prev: p.appetito_revenue, hint: "Comision × produse × 1,21 + transport (când livrăm noi) + taxa aplicației — aceeași formulă ca în Statistici."},
    {label: "Timp mediu total (comandă → livrat)", value: minutes(c.average_total_min), cur: c.average_total_min, prev: p.average_total_min, kind: "minutes", better: "lower"},
    {label: "Rata de anulare", value: pct(c.cancellation_rate, 1), cur: c.cancellation_rate, prev: p.cancellation_rate, kind: "pp", better: "lower", hint: `${int(c.canceled)} anulate din ${int(c.placed)} plasate.`},
  ];
  return (
    <div className="sv2-kpis">
      {tiles.map((t) => (
        <div key={t.label} className="sv2-card sv2-kpi" title={t.hint || ""}>
          <div className="sv2-kpi-label">{t.label}</div>
          <div className="sv2-kpi-value">{t.value}</div>
          <div className="sv2-kpi-change">
            <Change current={t.cur} previous={t.prev} kind={t.kind} better={t.better} available={available}/>
            <span className="sv2-muted"> {cmpLabel}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RevenueChart({data, available, cmpRange}) {
  const current = data.current;
  const buckets = current.buckets;
  const comparison = data.comparison.buckets;
  const labels = buckets.map((b) => (current.hourly ? `${b.key}:00` : dayLabel(b.key)));
  const revenue = buckets.map((b) => b.revenue);
  const showCmp = available && comparison.some((b) => b.revenue > 0);
  const cmpValues = buckets.map((_, i) => (comparison[i] ? comparison[i].revenue : null));
  const mean = revenue.length ? revenue.reduce((a, b) => a + b, 0) / revenue.length : 0;
  const series = [{name: "Încasări", type: "column", data: revenue}];
  if (showCmp) {
    series.push({name: `Comparație (${rangeLabel(cmpRange[0], cmpRange[1])})`, type: "line", data: cmpValues});
  }
  const options = {
    chart: {toolbar: {show: false}, fontFamily: "inherit", animations: {enabled: false}},
    colors: ["#DA7D46", "#7a6f69"],
    stroke: {width: [0, 2], curve: "straight", dashArray: [0, 0]},
    markers: {size: showCmp && buckets.length <= 31 ? [0, 3] : 0},
    plotOptions: {bar: {columnWidth: "65%", borderRadius: 3}},
    dataLabels: {enabled: false},
    xaxis: {categories: labels, tickAmount: Math.min(labels.length, 12), labels: {rotate: 0, hideOverlappingLabels: true}},
    yaxis: {min: 0, tickAmount: 4, labels: {formatter: (v) => compactLei(v)}},
    annotations: {yaxis: [{y: mean, borderColor: "#999", strokeDashArray: 4, label: {text: `Medie ${compactLei(mean)}`, style: {background: "transparent", color: "#888"}}}]},
    legend: {position: "top", horizontalAlign: "left"},
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({dataPointIndex}) => {
        const b = buckets[dataPointIndex];
        const cmp = comparison[dataPointIndex];
        const avg = b.delivered ? b.revenue / b.delivered : 0;
        let cmpLine = "";
        if (showCmp && cmp) {
          const change = cmp.revenue ? ` (${b.revenue >= cmp.revenue ? "+" : ""}${((b.revenue / cmp.revenue - 1) * 100).toFixed(1)}%)` : "";
          cmpLine = `<div>Comparație (${current.hourly ? `${cmp.key}:00` : dayLabel(cmp.key)}): ${lei(cmp.revenue)}${change}</div>`;
        }
        return `<div class="sv2-tooltip"><strong>${labels[dataPointIndex]}</strong><div>Încasări: ${lei(b.revenue)}</div>`
          + `<div>Comenzi: ${int(b.delivered)}</div><div>Medie: ${lei(avg, 2)}</div>${cmpLine}</div>`;
      },
    },
  };
  return (
    <div className="sv2-card">
      <div className="sv2-card-header"><h3>Încasări</h3><span className="sv2-muted">{current.hourly ? "pe oră" : "pe zi"}</span></div>
      <Chart key={`${labels.length}-${showCmp}`} options={options} series={series} type="line" height={320}/>
    </div>
  );
}

function Restaurants({rows, sort, onSort, available}) {
  const key = sort === "orders" ? "delivered" : "revenue";
  const top = [...rows].sort((a, b) => b[key] - a[key]).slice(0, 10);
  const max = top.length ? top[0][key] : 0;
  return (
    <div className="sv2-card">
      <div className="sv2-card-header">
        <h3>Top restaurante</h3>
        <Toggle options={[{id: "orders", label: "Comenzi"}, {id: "revenue", label: "Încasări"}]} value={sort} onChange={onSort}/>
      </div>
      <table className="sv2-table">
        <thead><tr><th>Restaurant</th><th>Comenzi</th><th>Încasări</th><th>Δ comenzi</th><th className="sv2-wide">Medie</th><th className="sv2-wide" title="De la plasarea comenzii până o marchează restaurantul gata">Preparare</th></tr></thead>
        <tbody>
        {top.map((r) => (
          <tr key={r.id}>
            <td>{r.name}</td>
            {sort === "orders" ? <BarCell value={r.delivered} max={max}>{int(r.delivered)}</BarCell> : <td>{int(r.delivered)}</td>}
            {sort === "revenue" ? <BarCell value={r.revenue} max={max}>{lei(r.revenue)}</BarCell> : <td>{lei(r.revenue)}</td>}
            <td>{available ? <Delta value={r.delivered - r.cmp_delivered}/> : <span className="sv2-muted">—</span>}</td>
            <td className="sv2-wide">{lei(r.delivered ? r.revenue / r.delivered : 0, 2)}</td>
            <td className="sv2-wide">{minutes(r.prep_min)}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}

function Customers({rows, sort, onSort}) {
  const key = sort === "orders" ? "delivered" : "spent";
  const top = [...rows].sort((a, b) => b[key] - a[key]).slice(0, 10);
  const max = top.length ? top[0][key] : 0;
  return (
    <div className="sv2-card">
      <div className="sv2-card-header">
        <h3>Top clienți</h3>
        <Toggle options={[{id: "orders", label: "Comenzi"}, {id: "spent", label: "Bani cheltuiți"}]} value={sort} onChange={onSort}/>
      </div>
      <table className="sv2-table">
        <thead><tr><th>Client</th><th>Comenzi</th><th>Cheltuit</th><th className="sv2-wide">Medie</th><th className="sv2-wide">Ultima comandă</th></tr></thead>
        <tbody>
        {top.map((c) => (
          <tr key={c.id}>
            <td>
              <a href={`/users?id=${c.id}`} className="sv2-link">{c.name || "Fără nume"}</a>
              <div className="sv2-muted sv2-small">#{c.id}</div>
            </td>
            {sort === "orders" ? <BarCell value={c.delivered} max={max}>{int(c.delivered)}</BarCell> : <td>{int(c.delivered)}</td>}
            {sort === "spent" ? <BarCell value={c.spent} max={max}>{lei(c.spent)}</BarCell> : <td>{lei(c.spent)}</td>}
            <td className="sv2-wide">{lei(c.delivered ? c.spent / c.delivered : 0, 2)}</td>
            <td className="sv2-wide">{formatDate(c.last_order)}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}

function Rating({likes, dislikes}) {
  const votes = likes + dislikes;
  if (!votes) {
    return <span className="sv2-muted">—</span>;
  }
  const share = likes / votes;
  const tone = share >= 0.9 ? "sv2-good" : share >= 0.8 ? "sv2-warn" : "sv2-bad";
  return <span className={tone} title={`${likes} like din ${votes} voturi`}>👍 {Math.round(share * 100)}%</span>;
}

function Couriers({rows, available}) {
  const top = rows.slice(0, 10);
  const max = top.length ? top[0].delivered : 0;
  return (
    <div className="sv2-card">
      <div className="sv2-card-header"><h3>Top livratori</h3></div>
      <table className="sv2-table">
        <thead><tr><th>Livrator</th><th>Comenzi</th><th>Δ comenzi</th><th className="sv2-wide" title="De la plasarea comenzii până la livrare">Timp total</th><th className="sv2-wide" title="De la ridicare până la livrare">Timp livrare</th><th>Rating</th></tr></thead>
        <tbody>
        {top.map((c) => (
          <tr key={c.id}>
            <td>{c.name}</td>
            <BarCell value={c.delivered} max={max}>{int(c.delivered)}</BarCell>
            <td>{available ? <Delta value={c.delivered - c.cmp_delivered}/> : <span className="sv2-muted">—</span>}</td>
            <td className="sv2-wide">{minutes(c.total_min)}</td>
            <td className="sv2-wide">{minutes(c.delivery_min)}</td>
            <td><Rating likes={c.likes} dislikes={c.dislikes}/></td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}

const WEEKDAYS = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];
const HOURS = Array.from({length: 13}, (_, i) => i + 10);

function Heatmap({heatmap}) {
  const rows = WEEKDAYS.map((label, i) => ({label, cells: heatmap[i]})).filter((r) => r.cells.some((n) => n > 0));
  const max = Math.max(1, ...rows.flatMap((r) => HOURS.map((h) => r.cells[h])));
  return (
    <div className="sv2-card">
      <div className="sv2-card-header"><h3>Când comandă clienții</h3><span className="sv2-muted">comenzi livrate, după ora plasării</span></div>
      {rows.length === 0 ? <div className="sv2-muted">Nicio comandă.</div> : (
        <div className="sv2-heatmap" style={{gridTemplateColumns: `40px repeat(${HOURS.length}, 1fr)`}}>
          <span/>
          {HOURS.map((h) => <span key={h} className="sv2-heat-hour">{h}</span>)}
          {rows.map((r) => (
            <React.Fragment key={r.label}>
              <span className="sv2-heat-day">{r.label}</span>
              {HOURS.map((h) => {
                const n = r.cells[h];
                return (
                  <span
                    key={h}
                    className="sv2-heat-cell"
                    title={`${r.label}, ${h}:00–${h + 1}:00 · ${n} comenzi`}
                    style={n ? {background: `rgba(218, 125, 70, ${0.12 + 0.88 * (n / max)})`} : undefined}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function Split({title, left, leftLabel, rightLabel}) {
  return (
    <div className="sv2-split">
      <div className="sv2-split-title">{title}</div>
      {left == null ? <div className="sv2-muted">—</div> : (
        <>
          <div className="sv2-split-bar">
            <div style={{width: `${left * 100}%`}}/>
          </div>
          <div className="sv2-split-labels">
            <span>{leftLabel} {pct(left)}</span>
            <span>{rightLabel} {pct(1 - left)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function Mix({period}) {
  return (
    <div className="sv2-card">
      <div className="sv2-card-header"><h3>Clienți și plăți</h3><span className="sv2-muted">comenzi livrate</span></div>
      <Split title="Metodă de plată" left={period.card_share} leftLabel="Card" rightLabel="Numerar"/>
      <Split title="Clienți" left={period.new_share} leftLabel="Noi" rightLabel="Recurenți"/>
      <div className="sv2-muted sv2-small">„Noi” = prima comandă livrată a clientului. Comenzile introduse de restaurante de pe tabletă nu sunt incluse.</div>
    </div>
  );
}

const TOP_CATEGORIES = 12;

function Categories({categories, sort, onSort, showAll, onShowAll}) {
  const key = sort === "qty" ? "qty" : "value";
  const all = [...categories].sort((a, b) => b[key] - a[key]);
  const total = all.reduce((sum, c) => sum + c[key], 0);
  const sorted = showAll ? all : all.slice(0, TOP_CATEGORIES);
  return (
    <div className="sv2-section">
      <div className="sv2-card-header">
        <h3>Top categorii vândute</h3>
        <Toggle options={[{id: "qty", label: "Bucăți"}, {id: "value", label: "Valoare"}]} value={sort} onChange={onSort}/>
      </div>
      <div className="sv2-categories">
        {sorted.map((c, i) => {
          const products = [...c.products].sort((a, b) => b[key] - a[key]).slice(0, 10);
          const max = products.length ? products[0][key] : 0;
          const share = total ? c[key] / total : 0;
          return (
            <div key={c.name} className="sv2-card sv2-category">
              <div className="sv2-category-head">
                <span className="sv2-rank">#{i + 1}</span>
                <strong>{c.name}</strong>
              </div>
              <div className="sv2-muted sv2-small">
                {int(c.qty)} buc. · {lei(c.value)} · {pct(share)} din {sort === "qty" ? "bucăți" : "valoare"}
              </div>
              <div className="sv2-share"><div style={{width: `${share * 100}%`}}/></div>
              <ol className="sv2-products">
                {products.map((p) => (
                  <li key={p.id}>
                    <div className="sv2-product-name">
                      {p.name}
                      <span className="sv2-muted sv2-small">{p.restaurant}</span>
                    </div>
                    <div className="sv2-product-value">
                      <div className="sv2-bar" style={{width: `${max ? (p[key] / max) * 100 : 0}%`}}/>
                      <span>{sort === "qty" ? int(p.qty) : lei(p.value)}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
      {!showAll && all.length > TOP_CATEGORIES && (
        <button className="sv2-more" onClick={onShowAll}>Arată toate categoriile ({all.length})</button>
      )}
    </div>
  );
}

const FLEET_TABS = [
  {id: "km", label: "Km"},
  {id: "repairs", label: "Reparații"},
  {id: "fuel", label: "Consum"},
  {id: "odometer", label: "Km de la bord"},
];

function Fleet({cars, tab, onTab, rangeLabel: periodLabel}) {
  const totalKm = cars.reduce((sum, c) => sum + c.km, 0);
  const repairs = cars.reduce((sum, c) => sum + c.service_cost, 0);
  const liters = cars.reduce((sum, c) => sum + c.fuel_liters, 0);
  const fuelCost = cars.reduce((sum, c) => sum + c.fuel_cost, 0);
  const avgConsumption = totalKm > 0 ? (liters / totalKm) * 100 : null;
  const consumption = (c) => (c.km > 0 && c.fuel_liters > 0 ? (c.fuel_liters / c.km) * 100 : null);
  const sorters = {
    km: (c) => c.km,
    repairs: (c) => c.service_cost,
    fuel: (c) => consumption(c) || 0,
    odometer: (c) => c.odometer || 0,
  };
  const sorted = [...cars].sort((a, b) => sorters[tab](b) - sorters[tab](a));
  const n = (v, d = 0) => Number(v || 0).toLocaleString("ro-RO", {maximumFractionDigits: d});
  return (
    <div className="sv2-card">
      <div className="sv2-card-header">
        <h3>Flota de mașini</h3>
        <span className="sv2-muted">{periodLabel} · fără comparație</span>
      </div>
      <div className="sv2-fleet-figures">
        <div><span className="sv2-muted">Km parcurși, toată flota</span><strong>{n(totalKm)} km</strong></div>
        <div><span className="sv2-muted">Cost reparații</span><strong>{lei(repairs)}</strong></div>
        <div><span className="sv2-muted">Consum mediu</span><strong>{avgConsumption == null ? "—" : `${n(avgConsumption, 1)} L/100 km`}</strong></div>
        <div><span className="sv2-muted">Cost pe km</span><strong>{totalKm > 0 ? lei((fuelCost + repairs) / totalKm, 2) : "—"}</strong></div>
      </div>
      <Toggle options={FLEET_TABS} value={tab} onChange={onTab}/>
      <table className="sv2-table sv2-fleet">
        <thead>
        <tr>
          <th>#</th><th>Mașină</th>
          {tab === "km" && <><th>Km parcurși</th><th>Medie pe zi activă</th><th>Zile pe drum</th></>}
          {tab === "repairs" && <><th>Cost reparații</th><th>Intervenții</th><th>Ultima intervenție</th><th>Cost pe km</th></>}
          {tab === "fuel" && <><th>Consum L/100 km</th><th>Litri</th><th>Cost carburant</th><th>Alimentări</th></>}
          {tab === "odometer" && <><th>Km la bord</th><th>Citire din</th><th>Până la revizie</th></>}
        </tr>
        </thead>
        <tbody>
        {sorted.map((c, i) => {
          const cons = consumption(c);
          const high = cons != null && avgConsumption != null && cons > avgConsumption * 1.15;
          const left = c.km_to_service;
          return (
            <tr key={c.id}>
              <td>{i + 1}</td>
              <td>{c.name}<div className="sv2-muted sv2-small">{c.plate}</div></td>
              {tab === "km" && <>
                <td>{n(c.km)} km</td>
                <td>{c.active_days ? `${n(c.km / c.active_days)} km` : "—"}</td>
                <td>{c.active_days}</td>
              </>}
              {tab === "repairs" && <>
                <td>{lei(c.service_cost)}</td>
                <td>{c.services}</td>
                <td>{c.services ? <>{c.last_service || "—"}<div className="sv2-muted sv2-small">{c.last_service_at}</div></> : <span className="sv2-muted">Niciuna</span>}</td>
                <td>{c.km > 0 ? lei(c.service_cost / c.km, 2) : "—"}</td>
              </>}
              {tab === "fuel" && <>
                <td className={high ? "sv2-bad" : ""}>{cons == null ? "—" : `${n(cons, 1)}`}</td>
                <td>{n(c.fuel_liters, 1)} L</td>
                <td>{lei(c.fuel_cost)}</td>
                <td>{c.fuelings}</td>
              </>}
              {tab === "odometer" && <>
                <td>{c.odometer == null ? <span className="sv2-muted">fără revizie înregistrată</span> : `${n(c.odometer)} km`}</td>
                <td>{c.odometer_at || "—"}</td>
                <td>
                  {left == null ? <span className="sv2-muted">—</span>
                    : left <= 0 ? <span className="sv2-bad">Depășită</span>
                      : <span className={left < 1500 ? "sv2-warn" : ""}>{n(left)} km</span>}
                </td>
              </>}
            </tr>
          );
        })}
        </tbody>
      </table>
    </div>
  );
}
